import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  GraduationCap,
  Users,
  BarChart3,
  Target,
  Brain,
  BookOpen,
  Monitor,
  Lightbulb,
  CheckCircle,
  Globe,
  Award
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

const services = [
  {
    id: 'teacher-development',
    icon: Users,
    title: 'Teacher Development',
    subtitle: 'Building Excellence in Educators',
    description: 'Comprehensive programs for teacher training, professional development, and capacity building to enhance pedagogical excellence across all levels of education.',
    features: [
      'Pre-service and in-service teacher training programs',
      'Pedagogical skills enhancement and modern teaching methodologies',
      'Leadership development for school administrators',
      'Continuous professional development frameworks',
      'Mentorship and coaching programs',
      'Subject-specific capacity building'
    ]
  },
  {
    id: 'education-assessment',
    icon: BarChart3,
    title: 'Education System Assessment',
    subtitle: 'Evidence-Based System Analysis',
    description: 'Rigorous evaluation of education systems including curriculum analysis, institutional capacity assessment, and learning outcome measurement to identify gaps and opportunities.',
    features: [
      'National and sub-national education system diagnostics',
      'Curriculum review and alignment analysis',
      'Institutional capacity assessments',
      'Learning outcome measurement and benchmarking',
      'Education quality assurance frameworks',
      'Policy gap analysis and recommendations'
    ]
  },
  {
    id: 'education-me',
    icon: Target,
    title: 'Education Sector M&E',
    subtitle: 'Measuring What Matters',
    description: 'Designing and implementing robust monitoring and evaluation frameworks for education interventions, policies, and programs to ensure accountability and continuous improvement.',
    features: [
      'M&E framework design for education programs',
      'Indicator development and data collection systems',
      'Impact evaluation of education interventions',
      'Real-time monitoring dashboards',
      'Performance tracking and reporting systems',
      'Learning and adaptation mechanisms'
    ]
  },
  {
    id: 'edtech-ai',
    icon: Brain,
    title: 'Educational Technology & AI',
    subtitle: 'Innovation for Learning',
    description: 'Integrating cutting-edge technology and artificial intelligence solutions for personalized learning, adaptive assessments, and enhanced teaching methodologies.',
    features: [
      'AI-powered personalized learning platforms',
      'Adaptive assessment and testing systems',
      'Learning management system implementation',
      'Digital literacy and ICT integration strategies',
      'EdTech ecosystem development',
      'AI tools for teachers and administrators'
    ]
  }
];

const impactAreas = [
  {
    icon: Globe,
    title: 'System-Wide Impact',
    description: 'Transform entire education systems through evidence-based interventions and strategic planning.'
  },
  {
    icon: Award,
    title: 'Quality Outcomes',
    description: 'Measurable improvements in learning outcomes, teacher effectiveness, and institutional performance.'
  },
  {
    icon: Lightbulb,
    title: 'Innovation Integration',
    description: 'Seamlessly blend traditional pedagogy with cutting-edge educational technology and AI.'
  }
];

export const EducationPage = () => {
  return (
    <div data-testid="education-page" className="bg-white">
      {/* Hero Section */}
      <section className="section-padding pt-32 bg-gradient-to-br from-slate-50 via-teal-50/30 to-white">
        <div className="container-main">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            <motion.div variants={itemVariants} className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-teal-600 flex items-center justify-center">
                <GraduationCap size={28} className="text-white" />
              </div>
              <span className="font-mono text-xs uppercase tracking-widest text-teal-600">
                Education Research & Development
              </span>
            </motion.div>
            
            <motion.h1 
              variants={itemVariants}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-6"
            >
              Transforming Learning Systems for the Future
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8"
            >
              We apply our rigorous analytical approach to education sector strengthening, 
              combining evidence-based research with innovative solutions to enhance learning 
              outcomes and build resilient education systems across Africa and beyond.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="education-cta-primary"
              >
                Partner With Us
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/services"
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="education-cta-secondary"
              >
                View All Services
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
              Our Approach to Education Excellence
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              We bring the same analytical rigor and systemic thinking that drives our health 
              sector work to education transformation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {impactAreas.map((area, index) => (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-teal-500/20 border border-teal-500/30 flex items-center justify-center mx-auto mb-4">
                  <area.icon size={32} className="text-teal-400" />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-2">{area.title}</h3>
                <p className="text-slate-400 text-sm">{area.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="section-padding" data-testid="education-services">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-4 block">
              Our Services
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Comprehensive Education Solutions
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From teacher development to AI-powered learning systems, we offer end-to-end 
              solutions for education sector transformation.
            </p>
          </motion.div>

          <div className="space-y-12">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className={`grid lg:grid-cols-2 gap-8 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`education-service-${service.id}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-teal-100 flex items-center justify-center">
                      <service.icon size={24} className="text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-heading text-2xl font-semibold text-slate-900">
                        {service.title}
                      </h3>
                      <p className="text-teal-600 text-sm font-medium">{service.subtitle}</p>
                    </div>
                  </div>
                  <p className="text-slate-600 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-3">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle size={18} className="text-teal-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`bg-gradient-to-br from-slate-100 to-teal-50 p-8 lg:p-12 ${
                  index % 2 === 1 ? 'lg:order-1' : ''
                }`}>
                  <div className="aspect-square max-w-xs mx-auto flex items-center justify-center">
                    <service.icon size={120} className="text-teal-600/20" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why HSL for Education */}
      <section className="section-padding bg-slate-50">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-4 block">
                Why HSL
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                The HSL Advantage in Education
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Our experience in health systems strengthening has given us unique insights into 
                complex adaptive systems. We bring this expertise to education, understanding that 
                lasting change requires addressing multiple interconnected factors simultaneously.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                We don't just implement programs—we design systems for sustainability, building 
                local capacity and institutional frameworks that outlast any single intervention.
              </p>
              <Link
                to="/methodology"
                className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium transition-colors group"
              >
                Learn About Our Methodology
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { label: 'Evidence-Based', sublabel: 'Approach' },
                { label: 'Systems', sublabel: 'Thinking' },
                { label: 'Local', sublabel: 'Context' },
                { label: 'Sustainable', sublabel: 'Impact' },
              ].map((item, i) => (
                <div 
                  key={i}
                  className="bg-white border border-slate-200 p-6 text-center hover:border-teal-500 transition-colors"
                >
                  <span className="font-heading text-2xl text-teal-600 block">{item.label}</span>
                  <span className="text-slate-500 text-sm">{item.sublabel}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-teal-600 text-white">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
              Ready to Transform Education?
            </h2>
            <p className="text-teal-100 max-w-2xl mx-auto mb-8">
              Whether you're a government ministry, development partner, or educational institution, 
              we're ready to help you design and implement evidence-based education solutions.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 font-bold tracking-wide uppercase text-sm hover:bg-teal-50 transition-colors"
              data-testid="education-cta-bottom"
            >
              Start the Conversation
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
